import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { HINTS } from './hint-config.js';
import { HintDialog } from './HintDialog.js';
import { closeHintDialog, openHintDialog, useHintState } from './hint-store.js';
import { getHintTargets, subscribeHintTargets } from './hint-targets.js';

interface HintBeaconPosition {
  id: string;
  title: string;
  top: number;
  left: number;
}

/**
 * How far inside the host's top-end corner the beacon's centre sits. Keeping it inside the box
 * matters: a beacon centred on the corner of a full-width sidebar row escapes onto the sidebar's
 * edge and reads as unattached to any row.
 */
const BEACON_INSET = 9;
/** Extra inward shift for the shared sidebar beacon column (keeps dots off the rail edge). */
const SIDEBAR_BEACON_INSET = 20;

/**
 * Sidebar row used for beacon geometry. Prefer an explicit `data-hint-beacon-row` (Automations
 * head wraps a short flex-1 trigger inside a taller h-8 row); otherwise the host itself.
 */
function sidebarBeaconRow(element: HTMLElement): HTMLElement {
  const row = element.closest('[data-hint-beacon-row]');
  return row instanceof HTMLElement ? row : element;
}

/**
 * Horizontal pixel for the beacon centre. Sidebar-aligned tips share the nav content column
 * (nearest `.px-3`, else `.sidebar-root`) so hosts that are only `flex-1` next to a chevron/gear
 * still line up with full-width links.
 */
function resolveBeaconLeft(
  element: HTMLElement,
  hint: (typeof HINTS)[string],
  hostRight: number,
): number {
  const offsetX = hint.beaconOffset?.x ?? 0;
  if (hint.beaconAlign === 'sidebar') {
    const column = element.closest('.px-3') ?? element.closest('.sidebar-root');
    if (column instanceof HTMLElement) {
      return (
        column.getBoundingClientRect().right - SIDEBAR_BEACON_INSET + offsetX
      );
    }
  }
  return hostRight - BEACON_INSET + offsetX;
}

/**
 * Vertical pixel for the beacon centre. Sidebar tips use the row box so a short centered trigger
 * (Automations) matches full-row hosts (Dashboards / Decks).
 */
function resolveBeaconTop(
  element: HTMLElement,
  hint: (typeof HINTS)[string],
  hostTop: number,
): number {
  const offsetY = hint.beaconOffset?.y ?? 0;
  if (hint.beaconAlign === 'sidebar') {
    return (
      sidebarBeaconRow(element).getBoundingClientRect().top +
      BEACON_INSET +
      offsetY
    );
  }
  return hostTop + BEACON_INSET + offsetY;
}

/**
 * Topmost open dialog, if any. Menus are ignored on purpose: a composer `+` menu must not clear
 * sidebar beacons. Radix modal menus also set `pointer-events: none` on the page, which makes
 * `elementsFromPoint` lie about coverage — so menu-open state is never used as a hide signal.
 */
function topmostDialog(): HTMLElement | null {
  const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"]');
  return dialogs.length === 0 ? null : (dialogs[dialogs.length - 1] ?? null);
}

/**
 * Whether the dialog is not settled on screen: sliding in, or closing, which Radix marks with
 * `data-state="closed"` while it slides out. Beacons measured then would stay where the dialog was
 * or will be, such as the mobile drawer's dots over the home screen (MEDA-1280).
 */
function isDialogMoving(dialog: HTMLElement): boolean {
  if (dialog.getAttribute('data-state') === 'closed') return true;
  return (
    typeof dialog.getAnimations === 'function' &&
    dialog
      .getAnimations()
      .some((animation) => animation.playState === 'running')
  );
}

/**
 * True when the beacon point is not covered by another UI layer inside the same dialog (e.g. the
 * directory tip overlay over connector cards). Beacons live in a z-200 portal above dialogs, so
 * containment alone is not enough. An empty hit-stack (jsdom / no layout) keeps the beacon.
 */
function isBeaconPointExposed(
  host: HTMLElement,
  x: number,
  y: number,
): boolean {
  if (typeof document.elementsFromPoint !== 'function') return true;
  const stack = document.elementsFromPoint(x, y);
  if (stack.length === 0) return true;
  for (const node of stack) {
    if (!(node instanceof Element)) continue;
    if (node.classList.contains('hint-beacon')) continue;
    if (node.closest('[data-hint-beacon-layer]')) continue;
    return host === node || host.contains(node);
  }
  return false;
}

/**
 * The element and every container around it below the body. A target moves without changing its own
 * size when a container around it grows, such as the composer's "+" while the message gets longer, so
 * the beacon has to hear about each of them (MEDA-1142).
 */
function selfAndContainers(element: Element): Element[] {
  const chain: Element[] = [];
  for (
    let node: Element | null = element;
    node !== null && node !== document.body;
    node = node.parentElement
  ) {
    chain.push(node);
  }
  return chain;
}

/**
 * Portal overlay that renders a beacon dot at the top-end corner of every registered,
 * unseen hint target, plus the dialog for whichever hint a target's gate opened.
 *
 * Positions are measured fresh on every render from `getBoundingClientRect()` — never cached —
 * and a render is triggered on hint-target registry changes, window scroll/resize, the end of an
 * animation (a dialog that finished sliding in), a dialog's `data-state` change, and a
 * `ResizeObserver` on every registered target and the containers around it. Open menus do not hide page beacons; open dialogs
 * keep only beacons for hosts inside that dialog, and still suppress dots covered by an in-dialog
 * overlay.
 */
export function HintBeaconLayer(): ReactNode {
  const { tourLightsUnlocked, seenNodeIds, activeDialogHintId } =
    useHintState();
  const [, setReflowTick] = useState(0);
  const triggerReflow = useCallback(
    () => setReflowTick((tick) => tick + 1),
    [],
  );

  const targets = useSyncExternalStore(subscribeHintTargets, getHintTargets);

  useEffect(() => {
    window.addEventListener('scroll', triggerReflow, true);
    window.addEventListener('resize', triggerReflow);
    window.addEventListener('animationend', triggerReflow, true);
    return () => {
      window.removeEventListener('scroll', triggerReflow, true);
      window.removeEventListener('resize', triggerReflow);
      window.removeEventListener('animationend', triggerReflow, true);
    };
  }, [triggerReflow]);

  useEffect(() => {
    const observer = new ResizeObserver(triggerReflow);
    for (const { element } of targets) {
      for (const box of selfAndContainers(element)) observer.observe(box);
    }
    return () => observer.disconnect();
  }, [targets, triggerReflow]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.getAttribute('role') === 'dialog'
        ) {
          triggerReflow();
          return;
        }
        for (const node of mutation.addedNodes) {
          if (
            node instanceof Element &&
            (node.getAttribute('role') === 'dialog' ||
              node.querySelector('[role="dialog"]'))
          ) {
            triggerReflow();
            return;
          }
        }
        for (const node of mutation.removedNodes) {
          if (
            node instanceof Element &&
            (node.getAttribute('role') === 'dialog' ||
              node.querySelector('[role="dialog"]'))
          ) {
            triggerReflow();
            return;
          }
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });
    return () => observer.disconnect();
  }, [triggerReflow]);

  const openDialog = topmostDialog();
  const beacons: HintBeaconPosition[] =
    tourLightsUnlocked && !(openDialog !== null && isDialogMoving(openDialog))
      ? targets.flatMap(({ id, element }) => {
          const hint = HINTS[id];
          if (!hint || seenNodeIds.includes(id)) return [];
          if (openDialog !== null && !openDialog.contains(element)) return [];
          const rect = element.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return [];
          const top = resolveBeaconTop(element, hint, rect.top);
          const left = resolveBeaconLeft(element, hint, rect.right);
          if (openDialog !== null && !isBeaconPointExposed(element, left, top))
            return [];
          return [{ id, title: hint.title, top, left }];
        })
      : [];

  return createPortal(
    <>
      <div
        data-hint-beacon-layer
        className="pointer-events-none fixed inset-0 z-[200]"
      >
        {beacons.map(({ id, title, top, left }) => (
          <button
            key={id}
            type="button"
            tabIndex={-1}
            title={`${title} — click to learn`}
            aria-label={`${title} — onboarding hint`}
            className="hint-beacon"
            style={{ top, left }}
            onClick={() => openHintDialog(id)}
          />
        ))}
      </div>
      {activeDialogHintId !== null && (
        <HintDialog id={activeDialogHintId} open onClose={closeHintDialog} />
      )}
    </>,
    document.body,
  );
}
